import { useState, useEffect, useReducer, Reducer } from "react";
import axios, { AxiosRequestConfig } from "axios";
import createAuthRefreshInterceptor from "axios-auth-refresh";

// logging
const DEBUG = true;

/**
 * states
 */
export type State<T> = {
  loading: boolean;
  status?: number;
  text?: string;
  data?: T;
  error?: string;
};

/**
 * actions
 */
type Action<T> =
  | { type: "request" }
  | { type: "success"; status: number; text: string; data: T }
  | { type: "failure"; status: number; text: string; error: string };

/**
 * state reducer
 */
const reducer = <T>(state: State<T>, action: Action<T>): State<T> => {
  switch (action.type) {
    case "request":
      return { loading: true };
    case "success":
      return {
        loading: false,
        status: action.status,
        text: action.text,
        data: action.data,
      };
    case "failure":
      return {
        loading: false,
        status: action.status,
        text: action.text,
        error: action.error,
      };
  }
};

//
/**
 * Generic API Hook with authentication refresh
 * @param req
 * @param log
 */
export const useApi = <T>(req?: AxiosRequestConfig) => {
  const [request, setRequest] = useState(req);
  const [log] = useState(DEBUG);
  const [state, dispatch] = useReducer<Reducer<State<T>, Action<T>>>(reducer, {
    loading: false,
  });

  // set request (will trigger useEffect hook)
  const updateRequest = (req: AxiosRequestConfig) => setRequest(req);

  useEffect(() => {
    const refreshAuth = async (error: any) => {
      const config: AxiosRequestConfig = {
        headers: { "Content-Type": "application/json" },
        withCredentials: true,
      };

      // basic auth post request
      if (log) console.log("Authenticating to server ...");
      return axios
        .post("/api/auth", { username: "upload" }, config)
        .then((response) => {
          if (log) console.log("Auth response", response);
          Promise.resolve();
        });
    };

    const fetchData = async () => {
      if (!request) return;
      dispatch({ type: "request" });

      // Call API
      try {
        // create axios instance
        const client = axios.create();

        // create response interceptor (handle 401 responses)
        createAuthRefreshInterceptor(client, refreshAuth);

        // perform API request
        client
          .request(request)
          .then((response) => {
            if (log) console.log("Response", response);
            dispatch({
              type: "success",
              status: response.status,
              text: response.statusText,
              data: response.data,
            });
          })
          .catch((error) => {
            if (log) console.error(error);
            dispatch({
              type: "failure",
              status: error.response?.status || 0,
              text: error.response?.statusText || "unknown error",
              error,
            });
          });
      } catch (error) {
        if (log) console.error(error);
        dispatch({
          type: "failure",
          status: error.response?.status || 0,
          text: error.response?.statusText || "unknown error",
          error,
        });
      }
    };

    // call API
    if (!request) return;
    fetchData();
  }, [request, log]);

  return { state, updateRequest };
};
